import { Box, Button, Flex, Icon, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text } from '@chakra-ui/react'
import { useState, useRef, useEffect } from 'react'
import { PlayIcon, PauseCircleIcon, VolumeMaxIcon, VolumeXIcon } from '../../assets/icons'

interface VideoPlayerProps {
  url: string
  onRemove: () => void
  startPlay?: boolean
  stopPlay?: boolean
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, startPlay = true, stopPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(startPlay)
  const [currentTime, setCurrentTime] = useState(0)
  const [isMuted, setIsMuted] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load()
      if (isPlaying) {
        videoRef.current.play()
      }
    }
  }, [url])

  useEffect(() => {
    if (videoRef.current && stopPlay) {
      videoRef.current.pause
    }
  })

  useEffect(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.play()
    } else {
      videoRef.current?.pause()
      setIsPlaying(false)
    }
  }, [isPlaying, startPlay])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = isMuted ? 0 : 1
    }
  }, [isMuted])

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  const handleSliderChange = (value: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value
      setCurrentTime(value)
    }
  }

  const handleMuteToggle = () => {
    setIsMuted((prev) => !prev)
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  return (
    <Box position="relative" width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
      <video
        ref={videoRef}
        width="100%"
        height="100%"
        controls={false}
        onTimeUpdate={handleTimeUpdate}
        style={{ objectFit: 'contain', maxHeight: '100%' }}
        key={url}
      >
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        height="45px"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        transition="background 0.3s ease"
        background={isHovered || !isPlaying ? 'rgba(0, 0, 0, 0.7)' : 'transparent'}
      >
        {(isHovered || !isPlaying) && (
          <Flex
            position="absolute"
            bottom="4px"
            left="16px"
            right="16px"
            alignItems="center"
            justifyContent="space-between"
            px="4px"
          >
            <Button onClick={handlePlayPause} bg="transparent" variant="icon">
              <Icon boxSize="20px" color="white" filter="drop-shadow(0px 0px 4px rgba(0,0,0,0.8))">
                {isPlaying ? <PauseCircleIcon /> : <PlayIcon />}
              </Icon>
            </Button>

            <Slider
              flex="1"
              mx="8px"
              value={currentTime}
              min={0}
              max={videoRef.current?.duration || 100}
              onChange={handleSliderChange}
            >
              <SliderTrack bg="gray.300">
                <SliderFilledTrack bg="white" />
              </SliderTrack>
              <SliderThumb boxShadow="0px 0px 6px rgba(0,0,0,0.8)" />
            </Slider>

            <Text ml="12px" textStyle="l-xs" color="solid.0" filter="drop-shadow(0px 0px 4px rgba(0,0,0,0.8))">
              {formatTime(currentTime)}/{formatTime(videoRef.current?.duration || 100)}
            </Text>

            <Button onClick={handleMuteToggle} bg="transparent" variant="icon" ml="8px">
              <Icon boxSize="20px" color="white" filter="drop-shadow(0px 0px 4px rgba(0,0,0,0.8))">
                {isMuted ? <VolumeXIcon /> : <VolumeMaxIcon />}
              </Icon>
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  )
}

export default VideoPlayer
